<template>
    <Teleport to="body">
        <div class="ds-urv-backdrop" aria-hidden="true"></div>
        <section
            ref="overlayRef"
            class="ds-urv-overlay"
            role="dialog"
            aria-modal="true"
            aria-label="Отметки сотрудников"
            @keydown.tab="trapFocus"
        >
            <UrvKeyForm
                v-if="!department || isKeyFormOpen"
                :error-message="keyError"
                :is-submitting="isAuthorizing"
                @submit="authorizeKey"
                @close="emit('close')"
            />
            <UrvQrScreen
                v-else
                :department="department"
                @session-invalid="clearDepartment"
                @request-key="openKeyForm"
                @close="emit('close')"
            />
        </section>
    </Teleport>
</template>

<script setup lang="ts">
import { nextTick, onMounted, ref, watch } from 'vue'
import { authorizeUrvKey, type UrvDepartment } from '@/api/urv'
import UrvKeyForm from './UrvKeyForm.vue'
import UrvQrScreen from './UrvQrScreen.vue'

const storageKey = 'main-front:urv-department'
const department = ref<UrvDepartment | null>(getStoredDepartment())
const isAuthorizing = ref(false)
const isKeyFormOpen = ref(false)
const keyError = ref('')
const overlayRef = ref<HTMLElement | null>(null)
const focusableSelector = 'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

const emit = defineEmits<{
    (event: 'close'): void
}>()

function getStoredDepartment(): UrvDepartment | null {
    const storedValue = localStorage.getItem(storageKey)

    if (!storedValue) {
        return null
    }

    try {
        const parsedValue = JSON.parse(storedValue) as Partial<UrvDepartment>
        if (typeof parsedValue.id !== 'string' || typeof parsedValue.name !== 'string') {
            localStorage.removeItem(storageKey)
            return null
        }

        const departmentContext = { id: parsedValue.id, name: parsedValue.name }
        localStorage.setItem(storageKey, JSON.stringify(departmentContext))
        return departmentContext
    } catch {
        localStorage.removeItem(storageKey)
        return null
    }
}

async function authorizeKey(key: string) {
    isAuthorizing.value = true
    keyError.value = ''

    try {
        const authorizedDepartment = await authorizeUrvKey(key)
        const departmentContext = {
            id: authorizedDepartment.id,
            name: authorizedDepartment.name,
        }
        department.value = departmentContext
        isKeyFormOpen.value = false
        localStorage.setItem(storageKey, JSON.stringify(departmentContext))
    } catch (error) {
        keyError.value = error instanceof Error ? error.message : 'Не удалось авторизовать ключ'
    } finally {
        isAuthorizing.value = false
    }
}

function clearDepartment() {
    localStorage.removeItem(storageKey)
    department.value = null
    isKeyFormOpen.value = false
    keyError.value = 'URV-сессия недействительна. Введите новый ключ доступа.'
}

function openKeyForm() {
    isKeyFormOpen.value = true
    keyError.value = ''
}

function focusFirstControl() {
    nextTick(() => {
        const overlay = overlayRef.value
        const autofocusControl = overlay?.querySelector<HTMLElement>('[autofocus]:not([disabled])')
        const firstControl = overlay?.querySelector<HTMLElement>(focusableSelector)
        ;(autofocusControl || firstControl)?.focus()
    })
}

function trapFocus(event: KeyboardEvent) {
    const controls = Array.from(overlayRef.value?.querySelectorAll<HTMLElement>(
        focusableSelector,
    ) || [])
    const firstControl = controls[0]
    const lastControl = controls[controls.length - 1]

    if (!firstControl || !lastControl) return

    if (event.shiftKey && document.activeElement === firstControl) {
        event.preventDefault()
        lastControl.focus()
    } else if (!event.shiftKey && document.activeElement === lastControl) {
        event.preventDefault()
        firstControl.focus()
    }
}

onMounted(focusFirstControl)
watch([department, isKeyFormOpen], focusFirstControl)
</script>
